import { Edit, Group } from '@mui/icons-material';
import { Box, IconButton, Paper, Skeleton, Typography } from '@mui/material';
import React from 'react';

import { CommitteeInfoCardProps } from './types';

const CommitteeInfoCardSkeleton = () => {
  return (
    <Paper
      sx={{
        p: 2,
        mb: 2,
        boxShadow: 'none',
        border: '1px solid #e0e0e0',
        backgroundColor: '#fafafa',
        position: 'relative',
        borderRadius: '8px',
      }}
    >
      <Skeleton
        variant="circular"
        width={32}
        height={32}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
        }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Skeleton
          variant="circular"
          width={40}
          height={40}
          sx={{ backgroundColor: 'rgba(25, 118, 210, 0.08)' }}
        />
        <Box>
          <Skeleton variant="text" width={180} height={24} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width={120} height={16} />
        </Box>
      </Box>

      {/* Description skeleton */}
      <Skeleton variant="text" width="100%" height={16} sx={{ mb: 0.5 }} />
      <Skeleton variant="text" width="90%" height={16} sx={{ mb: 0.5 }} />
      <Skeleton variant="text" width="85%" height={16} />
    </Paper>
  );
};

const CommitteeInfoCard = ({
  comitee,
  onEditClick,
  canEdit,
  loading = false,
}: CommitteeInfoCardProps) => {
  const hasCommittee = comitee?.name;
  const mainColor = hasCommittee ? 'primary.main' : 'warning.main';
  const backgroundColor = hasCommittee
    ? 'rgba(25, 118, 210, 0.08)'
    : 'rgba(255, 152, 0, 0.08)';

  if (loading) return <CommitteeInfoCardSkeleton />;

  return (
    <Paper
      sx={{
        p: 2,
        mb: 2,
        boxShadow: 'none',
        border: '1px solid #e0e0e0',
        backgroundColor: '#fafafa',
        position: 'relative',
        borderRadius: '8px',
      }}
    >
      {canEdit && onEditClick && (
        <IconButton
          onClick={onEditClick}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: backgroundColor,
            '&:hover': {
              backgroundColor: backgroundColor,
            },
          }}
          size="small"
        >
          <Edit
            sx={{
              fontSize: '1rem',
              color: hasCommittee ? 'primary.main' : 'warning.main',
            }}
          />
        </IconButton>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Box
          sx={{
            backgroundColor: backgroundColor,
            borderRadius: '50%',
            p: 0.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Group
            sx={{
              fontSize: '1rem',
              color: mainColor,
            }}
          />
        </Box>
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              lineHeight: 1,
              fontSize: '1.1rem',
              color: mainColor,
            }}
          >
            {hasCommittee ? comitee.name : 'Sin comité asignado'}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: '0.8rem',
              lineHeight: 1,
            }}
          >
            {hasCommittee ? 'Comité de Aprobación' : 'Estado del comité'}
          </Typography>
        </Box>
      </Box>

      <Typography
        variant="body2"
        sx={{ color: 'text.secondary', lineHeight: 1.4 }}
      >
        {hasCommittee
          ? 'Este comité es responsable de revisar y aprobar las solicitudes según los criterios establecidos. Cada rol debe ser aprobado por los usuarios correspondientes.'
          : 'No se ha asignado un comité a esta solicitud. Una vez asignado, podrás ver los roles requeridos y el estado de las aprobaciones. El comité será asignado automáticamente según los criterios de la solicitud y los roles disponibles en el sistema.'}
      </Typography>
    </Paper>
  );
};

export default CommitteeInfoCard;
